import { getResponseStatus, JsonServiceClient } from '@servicestack/client';
import { useCallback, useMemo, useState } from 'react';
import { client } from '../api/client';
import { DeleteRegistration, DeleteRegistrationResponse, ResponseStatus } from '../api/dtos';

const serviceStackClient: JsonServiceClient = client;

type DeleteRegistrationRequest = (
    request: DeleteRegistration
) => Promise<DeleteRegistrationResponse>;

export const getDeleteRegistrationErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object') {
        const responseStatus = getResponseStatus(error) as ResponseStatus | null | undefined;

        if (responseStatus?.message) {
            return responseStatus.message;
        }
    }

    if (error instanceof Error && error.message) {
        return error.message;
    }

    return 'Could not delete registration. Please try again.';
};

export const executeDeleteRegistration = async (
    id: number | undefined,
    deleteRequest: DeleteRegistrationRequest,
    setPending: (pending: boolean) => void
): Promise<DeleteRegistrationResponse | undefined> => {
    if (id === undefined) {
        return undefined;
    }

    setPending(true);

    try {
        return await deleteRequest(new DeleteRegistration({ id }));
    } finally {
        setPending(false);
    }
};

export const useDeleteRegistration = (id?: number) => {
    const [pending, setPending] = useState<boolean>(false);

    const deleteRegistration = useCallback(
        (registrationId?: number) =>
            executeDeleteRegistration(
                registrationId ?? id,
                (request) => serviceStackClient.delete(request),
                setPending
            ),
        [id]
    );

    const deleteRegistrationService = useMemo(
        () => ({
            deleteRegistration,
            pending,
        }),
        [deleteRegistration, pending]
    );

    return deleteRegistrationService;
};

export default useDeleteRegistration;
